import { staticReportsQueries } from "constants/reports";
import reportsDefaultQueriesByMeta from "utils/reports-default-queries-by-meta";
import assignIdsToRows from "utils/reports-table-ids";
import syncColumns from "utils/sync-report-table-column";

import { IAction } from "../..";
import { ReportsAction } from "../../actions";
import {
  CorrelationMatrixSettings,
  GetMetaSuccessPayload,
  GetReportsDataResponse,
  IReports,
  ReportsQueries
} from "../../types";
import { reducer } from "../index";

reducer.createHandler(ReportsAction.GetMetaSuccess, (state, { payload }: IAction<GetMetaSuccessPayload>) => {
  // normalizeQueries is true only once on application load
  const { meta, normalizeQueries } = payload;

  const queries = normalizeQueries ? reportsDefaultQueriesByMeta(meta, state.reports.queries) : state.reports.queries;

  return {
    ...state,
    reports: {
      ...state.reports,
      queries,
      meta: { ...state.reports.meta, ...meta }
    }
  };
});

reducer.createHandler(ReportsAction.SetQueries, (state, { payload }: IAction<Partial<ReportsQueries>>) => {
  if (
    (state.reports.queries.originalCurrency && payload.columnBy === "year") ||
    payload.columnBy === "quarter" ||
    payload.columnBy === "month"
  ) {
    payload.originalCurrency = false;
  }

  // When turning ON originalCurrency: inject "Currency" as first group
  if (payload.originalCurrency) {
    const currentGroupBy =
      payload.groupBy !== undefined ? payload.groupBy : state.reports.queries.groupBy;

    // Remove "Currency" and "none" values (none is a placeholder for no grouping)
    const groupByWithoutCurrencyAndNone = currentGroupBy.filter(
      group => !group.includes("Currency") && !group.includes("none")
    );

    // Inject Currency as first level, preserve rest of hierarchy
    payload.groupBy = [["Currency"], ...groupByWithoutCurrencyAndNone];

    // Don't force rowBy - keep user's selection (removed rowBy = "deal" line)
  }

  // When turning OFF originalCurrency: remove "Currency" from groupBy
  if (state.reports.queries.originalCurrency && payload.originalCurrency === false) {
    const currentGroupBy = state.reports.queries.groupBy ?? [];
    const groupByWithoutCurrency = currentGroupBy.filter(group => !group.includes("Currency"));

    // If we end up with fewer than 2 groups, pad with "none" to maintain structure
    if (groupByWithoutCurrency.length === 1) {
      payload.groupBy = [["none"], ...groupByWithoutCurrency];
    } else {
      payload.groupBy = groupByWithoutCurrency;
    }
  }

  // Update auto-disable conditions: only disable if user explicitly changes currency selector
  if (state.reports.queries.originalCurrency && payload.currency) {
    payload.originalCurrency = false;
    // Also remove Currency from groupBy when auto-disabling
    const currentGroupBy = state.reports.queries.groupBy ?? [];
    const groupByWithoutCurrency = currentGroupBy.filter(group => !group.includes("Currency"));

    // If we end up with fewer than 2 groups, pad with "none" to maintain structure
    if (groupByWithoutCurrency.length === 1) {
      payload.groupBy = [["none"], ...groupByWithoutCurrency];
    } else {
      payload.groupBy = groupByWithoutCurrency;
    }
  }
  if (payload.measures || payload.calculatedFields || payload.characteristics) {
    payload.reportTableColumns = syncColumns(payload, state.reports.queries.reportTableColumns);
  }
  return {
    ...state,
    reports: {
      ...state.reports,
      queries: {
        ...state.reports.queries,
        ...payload
      }
    },
    userModule: {
      ...state.userModule,
      selectedBookmark: null
    }
  };
});

reducer.createHandler(
  ReportsAction.ReplaceQueries,
  (
    state,
    { payload }: IAction<{ queries: Partial<ReportsQueries>; options?: Partial<{ ignoreCurrency: boolean }> }>
  ) => {
    const { queries, options } = payload;
    const freshQueries: Partial<ReportsQueries> = { ...staticReportsQueries, ...queries };

    // ignoreCurrency is supposed to be used for deal reports.
    // The currency was set after deal fetched
    if (options?.ignoreCurrency && freshQueries.currency) {
      delete freshQueries["currency"];
    }

    return {
      ...state,
      reports: {
        ...state.reports,
        queries: { ...state.reports.queries, ...freshQueries },
        ignoreDefaultView: true
      }
    };
  }
);

reducer.createHandler(ReportsAction.SetIgnoreDefaultView, (state, { payload }: IAction<boolean>) => {
  return {
    ...state,
    reports: {
      ...state.reports,
      ignoreDefaultView: payload
    }
  };
});

reducer.createHandler(
  ReportsAction.SetGraphFormat,
  (state, { payload: { key, ...rest } }: IAction<{ key: string; measure?: string; groupBy?: string }>) => ({
    ...state,
    reports: {
      ...state.reports,
      queries: {
        ...state.reports.queries,
        graphDataFormation: {
          ...state.reports.queries.graphDataFormation,
          [key]: {
            ...state.reports.queries.graphDataFormation[key],
            ...rest
          }
        }
      }
    }
  })
);

reducer.createHandler(ReportsAction.GetDataSuccess, (state, { payload }: IAction<GetReportsDataResponse>) => {
  const { tableData, secondaryTableData } = payload;

  tableData && assignIdsToRows("primary-table", tableData.rows);
  secondaryTableData && assignIdsToRows("secondary-table", secondaryTableData.rows);

  return {
    ...state,
    reports: {
      ...state.reports,
      tableData: tableData || state.reports.tableData,
      secondaryTableData: secondaryTableData || state.reports.secondaryTableData
    }
  };
});

reducer.createHandler(ReportsAction.clearTableData, (state) => ({
  ...state,
  reports: {
    ...state.reports,
    tableData: null,
    secondaryTableData: null
  }
}));

reducer.createHandler(ReportsAction.ToggleExpandRow, (state, { payload }: IAction<string>) => {
  const newExpandedRows = new Set(state.reports.expandedRows);

  if (state.reports.expandedRows.has(payload)) {
    newExpandedRows.delete(payload);
  } else {
    newExpandedRows.add(payload);
  }

  return {
    ...state,
    reports: {
      ...state.reports,
      expandedRows: newExpandedRows
    }
  };
});

// reducer.createHandler(
//   ReportsAction.UpdateConfig,
//   (
//     state,
//     {
//       payload: { key, pageType, ...rest }
//     }: IAction<{
//       title: string;
//       filter: string;
//       key: string;
//       pageType: keyof IReportsSettings;
//     }>
//   ) => {
//     return {
//       ...state,
//       reports: {
//         ...state.reports,
//         config: {
//           ...state.reports.config,
//           [pageType]: {
//             ...state.reports.config[pageType],
//             [key]: { ...state.reports.config[pageType][key], ...rest }
//           }
//         }
//       }
//     };
//   }
// );

reducer.createHandler(ReportsAction.SetQuickViewMeta, (state, { payload }: IAction<IReports["quick_view_meta"]>) => ({
  ...state,
  reports: {
    ...state.reports,
    quick_view_meta: {
      ...state.reports.quick_view_meta,
      ...payload
    }
  }
}));

reducer.createHandler(ReportsAction.RefetchReport, (state, { payload }: IAction<IReports["refetchReports"]>) => ({
  ...state,
  reports: {
    ...state.reports,
    refetchReports: payload
  }
}));

reducer.createHandler<boolean>(ReportsAction.SetIsHedgeFunds, (state, { payload }) => ({
  ...state,
  reports: {
    ...state.reports,
    is_hedge_fund: payload
  }
}));

reducer.createHandler<{
  cubicType: keyof Pick<ReportsQueries, "cubicDeal" | "cubicSnapshot">;
  cubicKey: string;
  filter: string;
  title: string;
}>(ReportsAction.SetCubic, (state, { payload: { cubicType, cubicKey, ...rest } }) => ({
  ...state,
  reports: {
    ...state.reports,
    queries: {
      ...state.reports.queries,
      [cubicType]: {
        ...state.reports.queries[cubicType],
        [cubicKey]: { ...state.reports.queries[cubicType][cubicKey], ...rest }
      }
    }
  }
}));

reducer.createHandler(
  ReportsAction.SetCorrelationMatrixSettings,
  (state, { payload }: IAction<CorrelationMatrixSettings>) => ({
    ...state,
    reports: {
      ...state.reports,
      correlationMatrixSettings: { ...state.reports.correlationMatrixSettings, ...payload }
    }
  })
);
